import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Trash2, ArrowLeft, Image, Film, Upload, Play, Music, FileUp, X, RefreshCw, FolderUp, CheckCircle, AlertCircle, UserCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ManageGallery = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSingle, setShowSingle] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [formData, setFormData] = useState({ url: '', caption_en: '', caption_bn: '', category_en: 'General', category_bn: 'সাধারণ', media_type: 'image' });
  const [bulkCategory, setBulkCategory] = useState({ en: 'General', bn: 'সাধারণ' });
  const [bulkFiles, setBulkFiles] = useState([]);
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  const abortRef = useRef(false);

  const getToken = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return null; }
    return token;
  }, [navigate]);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch('/api/admin/gallery', { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); return; }
      if (res.ok) setItems(await res.json());
    } catch (e) { console.error('Fetch error:', e); }
    finally { setLoading(false); }
  }, [getToken, navigate]);

  useEffect(() => { fetchGallery(); fetchSubmissions(); }, [fetchGallery]);

  const fetchSubmissions = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch('/api/gallery/submissions', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setSubmissions(await res.json());
    } catch (e) { console.error(e); }
  };

  const approveSubmission = async (id) => {
    const token = getToken();
    try {
      const res = await fetch(`/api/gallery/submissions/${id}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Photo approved'); fetchSubmissions(); fetchGallery(); }
    } catch (e) { toast.error('Failed'); }
  };

  const rejectSubmission = async (id) => {
    if (!window.confirm('Reject this submission?')) return;
    const token = getToken();
    try {
      const res = await fetch(`/api/gallery/submissions/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Submission rejected'); fetchSubmissions(); }
    } catch (e) { toast.error('Failed'); }
  };

  // Single file upload + add to gallery
  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch('/api/admin/gallery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          url: formData.url,
          caption: { en: formData.caption_en, bn: formData.caption_bn },
          category: { en: formData.category_en, bn: formData.category_bn },
          media_type: formData.media_type,
        }),
      });
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); return; }
      if (res.ok) {
        toast.success('Media added!');
        setFormData({ url: '', caption_en: '', caption_bn: '', category_en: 'General', category_bn: 'সাধারণ', media_type: 'image' });
        setShowSingle(false);
        fetchGallery();
      }
    } catch (e) { toast.error('Error adding media'); }
  };

  const handleSingleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/gallery/upload', { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        const captionText = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');
        setFormData(prev => ({ ...prev, url: data.url, media_type: data.media_type, caption_en: captionText }));
        toast.success(`${data.media_type} uploaded!`);
      } else { toast.error('Upload failed'); }
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  // Bulk file selection
  const handleBulkFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBulkFiles(prev => [...prev, ...files]);
    toast.success(`${files.length} file(s) selected`);
    if (bulkInputRef.current) bulkInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setBulkFiles(prev => [...prev, ...files]);
      toast.success(`${files.length} file(s) dropped`);
    }
  };

  const removeBulkFile = (idx) => setBulkFiles(prev => prev.filter((_, i) => i !== idx));

  // CHUNKED BULK UPLOAD: Upload files one-by-one to avoid 413 errors
  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) { toast.error('No files selected'); return; }
    const token = getToken();
    if (!token) return;

    setUploading(true);
    abortRef.current = false;
    const total = bulkFiles.length;
    let success = 0;
    let failed = 0;
    setBulkProgress({ current: 0, total, success: 0, failed: 0 });

    for (let i = 0; i < bulkFiles.length; i++) {
      if (abortRef.current) break;

      const file = bulkFiles[i];
      setBulkProgress({ current: i + 1, total, success, failed });

      try {
        // Step 1: Upload the file
        const fd = new FormData();
        fd.append('file', file);
        const uploadRes = await fetch('/api/gallery/upload', { method: 'POST', body: fd });

        if (!uploadRes.ok) { failed++; continue; }
        const uploadData = await uploadRes.json();

        // Step 2: Create gallery entry
        const captionText = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
        const entryRes = await fetch('/api/admin/gallery', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            url: uploadData.url,
            caption: { en: captionText, bn: captionText },
            category: { en: bulkCategory.en, bn: bulkCategory.bn },
            media_type: uploadData.media_type,
          }),
        });

        if (entryRes.ok) { success++; } else { failed++; }
      } catch (e) {
        console.error(`Failed to upload ${file.name}:`, e);
        failed++;
      }

      setBulkProgress({ current: i + 1, total, success, failed });
    }

    setBulkProgress({ current: total, total, success, failed });

    if (failed === 0) {
      toast.success(`All ${success} file(s) uploaded successfully!`);
    } else {
      toast.warning(`${success} uploaded, ${failed} failed`);
    }

    setBulkFiles([]);
    setUploading(false);
    fetchGallery();
  };

  const handleAbortUpload = () => {
    abortRef.current = true;
    toast.info('Upload cancelled. Already uploaded files are saved.');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    const token = getToken();
    if (!token) return;
    await fetch(`/api/admin/gallery/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    toast.success('Deleted!');
    setItems(prev => prev.filter(i => i._id !== id));
  };

  const isVideo = (item) => item.media_type === 'video' || /\.(mp4|webm|ogg|mov|avi)$/i.test(item.url || '');
  const isAudio = (item) => item.media_type === 'audio' || /\.(mp3|wav|aac|flac|m4a|opus)$/i.test(item.url || '');

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const totalBulkSize = bulkFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="manage-gallery-page">
      {/* Header */}
      <div className="bg-party-green text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Manage Gallery ({items.length})</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchGallery} variant="ghost" size="sm" className="text-white hover:bg-white/20" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => { setShowBulk(true); setShowSingle(false); }} className="bg-yellow-500 hover:bg-yellow-600 text-black" data-testid="bulk-upload-btn">
            <FolderUp className="h-4 w-4 mr-2" /> Bulk Upload
          </Button>
          <Button onClick={() => { setShowSingle(true); setShowBulk(false); }} className="bg-white text-party-green hover:bg-gray-100" data-testid="add-single-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Single
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">

        {/* Pending Submissions */}
        {submissions.length > 0 && (
          <Card className="mb-6 border-t-4 border-t-amber-500" data-testid="submissions-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <UserCheck className="h-5 w-5" /> Community Submissions ({submissions.length} pending)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {submissions.map(sub => (
                  <div key={sub._id} className="border rounded-lg overflow-hidden bg-white shadow-sm" data-testid={`submission-${sub._id}`}>
                    <div className="aspect-square bg-gray-100">
                      {sub.media_type === 'video' ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800"><Play className="h-8 w-8 text-white" /></div>
                      ) : (
                        <img src={sub.url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-2 space-y-1">
                      <p className="text-xs font-medium text-gray-900 truncate">{sub.submitter_name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{sub.caption?.en || sub.original_filename}</p>
                      <div className="flex gap-1">
                        <Button size="sm" className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => approveSubmission(sub._id)} data-testid={`approve-${sub._id}`}>
                          <CheckCircle className="h-3 w-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => rejectSubmission(sub._id)} data-testid={`reject-${sub._id}`}>
                          <XCircle className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bulk Upload Form */}
        {showBulk && (
          <Card className="mb-6 border-t-4 border-t-yellow-500" data-testid="bulk-upload-form">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><FolderUp className="h-5 w-5" /> Bulk Media Upload</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowBulk(false); setBulkFiles([]); }}><X className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Category (English)</label>
                    <Input value={bulkCategory.en} onChange={e => setBulkCategory(p => ({...p, en: e.target.value}))} data-testid="bulk-category-en" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Category (Bengali)</label>
                    <Input value={bulkCategory.bn} onChange={e => setBulkCategory(p => ({...p, bn: e.target.value}))} data-testid="bulk-category-bn" />
                  </div>
                </div>

                {/* Drop Zone */}
                <div
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-party-green transition-colors bg-gray-50 cursor-pointer"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-party-green', 'bg-green-50'); }}
                  onDragLeave={e => { e.preventDefault(); e.currentTarget.classList.remove('border-party-green', 'bg-green-50'); }}
                  onDrop={handleDrop}
                  onClick={() => !uploading && bulkInputRef.current?.click()}
                  data-testid="bulk-drop-zone"
                >
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">Drag & drop files here or click to browse</p>
                  <p className="text-gray-400 text-sm mt-1">Photos, Videos, Audio — no size limit. Files upload one-by-one.</p>
                  <input ref={bulkInputRef} type="file" multiple accept="image/*,video/*,audio/*" className="hidden" onChange={handleBulkFilesSelect} />
                </div>

                {/* Selected Files List */}
                {bulkFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-700">{bulkFiles.length} file(s) ready ({formatSize(totalBulkSize)} total)</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                      {bulkFiles.map((f, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded text-sm hover:bg-gray-50">
                          <div className="flex items-center gap-2 min-w-0">
                            {f.type?.startsWith('video') ? <Film className="h-4 w-4 text-blue-500 flex-shrink-0" /> :
                             f.type?.startsWith('audio') ? <Music className="h-4 w-4 text-purple-500 flex-shrink-0" /> :
                             <Image className="h-4 w-4 text-green-500 flex-shrink-0" />}
                            <span className="truncate max-w-[300px]">{f.name}</span>
                            <span className="text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
                          </div>
                          {!uploading && (
                            <button onClick={() => removeBulkFile(idx)} className="text-red-400 hover:text-red-600 flex-shrink-0 ml-2">
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Progress Bar */}
                    {uploading && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">
                            Uploading file {bulkProgress.current} of {bulkProgress.total}...
                          </p>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="flex items-center gap-1 text-green-600"><CheckCircle className="h-3 w-3" /> {bulkProgress.success}</span>
                            {bulkProgress.failed > 0 && <span className="flex items-center gap-1 text-red-500"><AlertCircle className="h-3 w-3" /> {bulkProgress.failed}</span>}
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className="bg-party-green h-3 rounded-full transition-all duration-300" style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{Math.round((bulkProgress.current / bulkProgress.total) * 100)}% complete</p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      {!uploading ? (
                        <>
                          <Button onClick={handleBulkUpload} className="bg-party-green hover:bg-green-700" data-testid="start-bulk-upload-btn">
                            <Upload className="h-4 w-4 mr-2" /> Upload All ({bulkFiles.length})
                          </Button>
                          <Button variant="outline" onClick={() => setBulkFiles([])}>Clear All</Button>
                        </>
                      ) : (
                        <Button variant="destructive" onClick={handleAbortUpload}>
                          <X className="h-4 w-4 mr-2" /> Cancel Upload
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Single Add Form */}
        {showSingle && (
          <Card className="mb-6 border-t-4 border-t-party-green" data-testid="single-upload-form">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add Single Media</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowSingle(false)}><X className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSingleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 flex gap-3">
                  {['image', 'video', 'audio'].map(mt => (
                    <Button key={mt} type="button" variant={formData.media_type === mt ? 'default' : 'outline'}
                      className={formData.media_type === mt ? 'bg-party-green' : ''}
                      onClick={() => setFormData(p => ({...p, media_type: mt}))}>
                      {mt === 'video' ? <Film className="h-4 w-4 mr-2" /> : mt === 'audio' ? <Music className="h-4 w-4 mr-2" /> : <Image className="h-4 w-4 mr-2" />}
                      {mt.charAt(0).toUpperCase() + mt.slice(1)}
                    </Button>
                  ))}
                </div>

                <div className="md:col-span-2 flex gap-4 items-end">
                  <div className="flex-1">
                    <Input placeholder="Media URL" value={formData.url} onChange={e => setFormData({...formData, url: e.target.value})} required data-testid="gallery-url-input" />
                  </div>
                  <Button type="button" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*" className="hidden" onChange={handleSingleUpload} />
                </div>

                {formData.url && (
                  <div className="md:col-span-2">
                    {formData.media_type === 'video' ? <video src={formData.url} controls className="h-48 rounded-lg" />
                     : formData.media_type === 'audio' ? <audio src={formData.url} controls className="w-full" />
                     : <img src={formData.url} alt="Preview" className="h-48 object-cover rounded-lg" onError={e => { e.target.style.display = 'none'; }} />}
                  </div>
                )}

                <Input placeholder="Caption (English)" value={formData.caption_en} onChange={e => setFormData({...formData, caption_en: e.target.value})} data-testid="gallery-caption-en" />
                <Input placeholder="Caption (Bengali)" value={formData.caption_bn} onChange={e => setFormData({...formData, caption_bn: e.target.value})} data-testid="gallery-caption-bn" />
                <Input placeholder="Category (English)" value={formData.category_en} onChange={e => setFormData({...formData, category_en: e.target.value})} />
                <Input placeholder="Category (Bengali)" value={formData.category_bn} onChange={e => setFormData({...formData, category_bn: e.target.value})} />
                <div className="md:col-span-2 flex gap-2">
                  <Button type="submit" className="bg-party-green hover:bg-green-700" data-testid="gallery-submit-btn">Add to Gallery</Button>
                  <Button type="button" variant="outline" onClick={() => setShowSingle(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-green mx-auto"></div></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FileUp className="h-16 w-16 mx-auto mb-4" />
            <p className="text-lg">No gallery items found.</p>
            <p className="text-sm mt-1">Click "Bulk Upload" or "Add Single" to start adding media.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {items.map((item) => (
              <Card key={item._id} className="overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`gallery-card-${item._id}`}>
                <div className="aspect-square overflow-hidden relative bg-gray-100">
                  {isVideo(item) ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                      <video src={item.url} className="w-full h-full object-cover" muted preload="metadata" />
                      <div className="absolute inset-0 flex items-center justify-center"><Play className="h-10 w-10 text-white/80" /></div>
                    </div>
                  ) : isAudio(item) ? (
                    <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200 flex flex-col items-center justify-center p-4">
                      <Music className="h-12 w-12 text-purple-500 mb-2" />
                      <audio src={item.url} controls className="w-full" />
                    </div>
                  ) : (
                    <img src={item.url} alt={t(item.caption)} className="w-full h-full object-cover" loading="lazy"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(item._id)} data-testid={`delete-${item._id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {(isVideo(item) || isAudio(item)) && (
                    <span className={`absolute top-2 left-2 text-white text-xs px-2 py-0.5 rounded ${isVideo(item) ? 'bg-blue-500' : 'bg-purple-500'}`}>
                      {isVideo(item) ? 'Video' : 'Audio'}
                    </span>
                  )}
                </div>
                <CardContent className="p-3">
                  <p className="text-sm font-medium truncate">{t(item.caption) || 'No caption'}</p>
                  <p className="text-xs text-gray-500">{t(item.category)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageGallery;
